import { toast } from 'sonner';

/**
 * Handles API errors and returns a user-friendly message
 * @param {Error} error - The error object from axios
 * @returns {string} - User-friendly error message
 */
export const handleApiError = (error) => {
    // Network error
    if (!error.response) {
        return 'Erreur de connexion. Vérifiez votre connexion internet.';
    }

    // Server error
    if (error.response.status >= 500) {
        return 'Erreur serveur. Veuillez réessayer plus tard.';
    }

    // Client error with detail
    if (error.response.data?.detail) {
        return error.response.data.detail;
    }

    // Client error with message
    if (error.response.data?.message) {
        return error.response.data.message;
    }

    // Rate limit error
    if (error.response.status === 429) {
        return 'Trop de tentatives. Veuillez patienter avant de réessayer.';
    }

    // Unauthorized
    if (error.response.status === 401) {
        return 'Session expirée. Veuillez vous reconnecter.';
    }

    // Forbidden
    if (error.response.status === 403) {
        return 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    }

    // Not found
    if (error.response.status === 404) {
        return 'Ressource non trouvée.';
    }

    // Default error
    return 'Une erreur est survenue. Veuillez réessayer.';
};

/**
 * Extracts error message from various error formats
 * @param {Error|string} error - The error
 * @returns {string} - Error message
 */
export const getErrorMessage = (error) => {
    if (typeof error === 'string') {
        return error;
    }

    if (error?.response) {
        return handleApiError(error);
    }

    if (error?.message) {
        return error.message;
    }

    return 'Une erreur inattendue est survenue.';
};

/**
 * Shows an error toast with proper formatting
 * @param {Error|string} error - The error
 * @param {string} title - Optional title for the toast
 */
export const showErrorToast = (error, title = 'Erreur') => {
    const message = getErrorMessage(error);
    toast.error(title, {
        description: message,
        duration: 5000,
    });
};

/**
 * Shows a success toast
 * @param {string} message - Success message
 * @param {string} title - Optional title
 */
export const showSuccessToast = (message, title = 'Succès') => {
    toast.success(title, {
        description: message,
        duration: 3000,
    });
};

/**
 * Shows an info toast
 * @param {string} message - Info message
 * @param {string} title - Optional title
 */
export const showInfoToast = (message, title = 'Information') => {
    toast.info(title, {
        description: message,
        duration: 4000,
    });
};

/**
 * Shows a warning toast
 * @param {string} message - Warning message
 * @param {string} title - Optional title
 */
export const showWarningToast = (message, title = 'Attention') => {
    toast.warning(title, {
        description: message,
        duration: 4000,
    });
};

/**
 * Shows a loading toast and returns the toast ID for dismissal
 * @param {string} message - Loading message
 * @returns {string|number} - Toast ID
 */
export const showLoadingToast = (message = 'Chargement...') => {
    return toast.loading(message);
};

/**
 * Dismisses a specific toast
 * @param {string|number} toastId - Toast ID to dismiss
 */
export const dismissToast = (toastId) => {
    toast.dismiss(toastId);
};

/**
 * Updates an existing toast (useful for loading -> success/error)
 * @param {string|number} toastId - Toast ID to update
 * @param {object} options - Toast options
 */
export const updateToast = (toastId, options) => {
    toast.dismiss(toastId);

    if (options.type === 'success') {
        showSuccessToast(options.description, options.title);
    } else if (options.type === 'error') {
        showErrorToast(options.description, options.title);
    }
};
